import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Chip, LinearProgress, Typography, Skeleton } from '@mui/material';
import { getActiveCampaigns, getCampaignProgress } from '../../api/incentiveApi';

const formatCurrency = (val) => `RM ${(val || 0).toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
// For Case-metric campaigns the value represents a count (no RM prefix).
const formatValueForMetric = (val, metric) => {
  if (metric === 'Revenue') return formatCurrency(val);
  const n = Number(val) || 0;
  return n.toLocaleString('en-MY');
};

const TYPE_COLORS = {
  Case: { bg: '#EFF6FF', color: '#1A8CFF', border: '#1A8CFF' },
  Travel: { bg: '#EFF6FF', color: '#1A8CFF', border: '#1A8CFF' },
  Item: { bg: '#EFF6FF', color: '#1A8CFF', border: '#1A8CFF' },
};

export default function ActiveCampaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [progressMap, setProgressMap] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getActiveCampaigns();
        const list = res.data ?? [];
        setCampaigns(list);

        // Fetch progress for each campaign
        const progressEntries = await Promise.allSettled(
          list.map(async (c) => {
            const pRes = await getCampaignProgress(c.campaignId);
            return [c.campaignId, pRes.data];
          })
        );
        const map = {};
        progressEntries.forEach((entry) => {
          if (entry.status === 'fulfilled') {
            const [id, data] = entry.value;
            map[id] = data;
          }
        });
        setProgressMap(map);
      } catch (err) {
        console.error('Error fetching active campaigns:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const getNextTierProgress = (progress) => {
    const tiers = progress?.tiers || progress?.tierProgress;
    if (!tiers?.length) return { percent: 0, label: '' };
    const nextTier = tiers.find((t) => !t.achieved);
    if (!nextTier) {
      const last = tiers[tiers.length - 1];
      return { percent: 100, label: `${last.tierName} achieved!` };
    }
    const pct = Math.round(nextTier.progressPercent ?? (nextTier.target > 0 ? Math.min(100, (nextTier.currentValue / nextTier.target) * 100) : 0));
    return { percent: pct, label: `${nextTier.tierName}: ${pct}%` };
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif", mb: 0.5 }}>
          Incentive Campaigns
        </Typography>
        <Typography sx={{ fontSize: '0.85rem', color: '#64748B', fontFamily: "'Poppins', sans-serif", mb: 3 }}>
          Your incentive campaigns
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {[1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={200} sx={{ borderRadius: '14px' }} />
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif", mb: 0.5 }}>
        Incentive Campaigns
      </Typography>
      <Typography sx={{ fontSize: '0.85rem', color: '#64748B', fontFamily: "'Poppins', sans-serif", mb: 3 }}>
        Your incentive campaigns
      </Typography>

      {campaigns.length === 0 ? (
        <Card sx={{ borderRadius: '14px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', p: 4, textAlign: 'center' }}>
          <Typography sx={{ color: '#94A3B8', fontFamily: "'Poppins', sans-serif" }}>
            No campaigns at the moment.
          </Typography>
        </Card>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
          {campaigns.map((c) => {
            const typeStyle = TYPE_COLORS[c.type] ?? TYPE_COLORS.Case;
            const progress = progressMap[c.campaignId];
            const tierInfo = getNextTierProgress(progress);
            const isEnded = new Date(c.endDate) < new Date();

            return (
              <Card
                key={c.campaignId}
                sx={{
                  borderRadius: '14px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                  borderTop: `4px solid ${typeStyle.border}`,
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.12)', transform: 'translateY(-2px)' },
                }}
                onClick={() => navigate(`/incentives/${c.campaignId}`)}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>
                      {c.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {isEnded && <Chip label="Ended" size="small" sx={{ backgroundColor: '#F1F5F9', color: '#64748B', fontWeight: 600, fontSize: '0.65rem', fontFamily: "'Poppins', sans-serif" }} />}
                      <Chip
                        label={c.type}
                        size="small"
                        sx={{
                          backgroundColor: typeStyle.bg,
                          color: typeStyle.color,
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          fontFamily: "'Poppins', sans-serif",
                        }}
                      />
                    </Box>
                  </Box>

                  <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontFamily: "'Poppins', sans-serif", mb: 1 }}>
                    {formatDate(c.startDate)} - {formatDate(c.endDate)}
                  </Typography>

                  {c.description && (
                    <Typography
                      sx={{
                        fontSize: '0.82rem',
                        color: '#475569',
                        fontFamily: "'Poppins', sans-serif",
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {c.description}
                    </Typography>
                  )}

                  {/* Revenue achieved */}
                  {progress && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#0F172A', fontFamily: "'Poppins', sans-serif" }}>
                        {progress.grandTotal != null ? formatValueForMetric(progress.grandTotal, c.metric) : (c.metric === 'Revenue' ? 'RM 0' : '0')} <span style={{ fontSize: '0.72rem', color: '#64748B', fontWeight: 500 }}>{c.metric === 'Revenue' ? 'achieved' : 'cases achieved'}</span>
                      </Typography>
                    </Box>
                  )}

                  {/* Progress bar */}
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontFamily: "'Poppins', sans-serif", fontWeight: 500 }}>
                        {tierInfo.label || 'Progress'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                        {tierInfo.percent}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={tierInfo.percent}
                      sx={{
                        height: 10, borderRadius: 5, backgroundColor: '#E2E8F0',
                        '& .MuiLinearProgress-bar': { borderRadius: 5, backgroundColor: tierInfo.percent >= 100 ? '#10B981' : typeStyle.color },
                      }}
                    />
                  </Box>

                  {/* Tier targets */}
                  {progress?.tiers && (
                    <Box sx={{ mb: 1.5 }}>
                      {(progress.tiers || []).map((t, i) => {
                        const remaining = Math.max(0, t.target - (progress.grandTotal || 0));
                        return (
                          <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3 }}>
                            <Typography sx={{ fontSize: '0.68rem', color: t.achieved ? '#10B981' : '#0F172A', fontFamily: "'Poppins', sans-serif", fontWeight: 500 }}>
                              {t.achieved ? `✓ ${t.tierName}` : <><span style={{ color: '#1A8CFF', fontWeight: 700 }}>{formatValueForMetric(remaining, c.metric)}</span> more to {t.tierName}</>}
                            </Typography>
                            <Typography sx={{ fontSize: '0.65rem', color: t.achieved ? '#10B981' : '#0F172A', fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                              {t.reward}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}

                  {/* Days left */}
                  {(() => {
                    const daysLeft = Math.max(0, Math.ceil((new Date(c.endDate) - new Date()) / (1000 * 60 * 60 * 24)));
                    return (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.72rem', color: isEnded ? '#EF4444' : '#F59E0B', fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                          {isEnded ? 'Campaign ended' : `${daysLeft} days left`}
                        </Typography>
                        <Typography sx={{ fontSize: '0.8rem', color: typeStyle.color, fontFamily: "'Poppins', sans-serif", fontWeight: 600 }}>
                          View Details →
                        </Typography>
                      </Box>
                    );
                  })()}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
